import { useState, useCallback, useEffect, useRef } from 'react';
import { useMessagingStore } from '@/store/messaging.store';
import { formatDistanceToNow } from 'date-fns';
import type { MessageWithDetails } from '@/types/messaging';

import FaIcon from '../common/FaIcon';
import SearchInput from '../common/SearchInput';
import Button, { IconButton } from '../common/Button';

interface MessageSearchProps {
  conversationId: string;
  onClose: () => void;
  onMessageClick?: (message: MessageWithDetails) => void;
}

export function MessageSearch({ conversationId, onClose, onMessageClick }: MessageSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localQuery, setLocalQuery] = useState('');
  const {
    messagesByConversation,
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    isSearching,
    setIsSearching,
    clearSearch,
  } = useMessagingStore();

  const messages = messagesByConversation[conversationId] || [];

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
    return () => {
      clearSearch();
    };
  }, [clearSearch]);

  // Search through messages
  const performSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      const searchLower = query.toLowerCase();

      const results = messages.filter((message) => {
        // Search in message content
        if (message.content?.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search in sender name
        if (message.sender?.name?.toLowerCase().includes(searchLower)) {
          return true;
        }
        return false;
      });

      // Sort by relevance (exact matches first, then by date)
      results.sort((a, b) => {
        const aExact = a.content?.toLowerCase() === searchLower;
        const bExact = b.content?.toLowerCase() === searchLower;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      setSearchResults(results);
      setIsSearching(false);
    },
    [messages, setSearchResults, setIsSearching]
  );

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localQuery);
      performSearch(localQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [localQuery, setSearchQuery, performSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim() || !text) return text;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 text-inherit rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className="absolute inset-0 z-20 bg-surface-elevated flex flex-col">
      {/* Search header */}
      <div className="px-4 py-3 border-b border-theme-primary">
        <div className="flex items-center gap-3">
          <IconButton
            icon={<FaIcon icon="chevron-left" className="w-5 h-5" />}
            label="Close search"
            variant="ghost"
            size="sm"
            onClick={onClose}
          />
          <div className="flex-1 relative">
            <SearchInput
              ref={inputRef as React.Ref<HTMLInputElement>}
              placeholder="Search messages..."
              aria-label="Search messages"
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pr-3 py-2 text-sm bg-surface-tertiary border-none"
            />
            {localQuery && (
              <IconButton
                icon={<FaIcon icon="xmark" className="w-4 h-4" />}
                label="Clear search"
                variant="ghost"
                size="sm"
                onClick={() => setLocalQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0 min-h-0 min-w-0"
              />
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {isSearching ? (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : searchQuery && searchResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-theme-tertiary">
            <FaIcon icon="face-frown" className="w-12 h-12 mb-3" />
            <p className="text-sm">No messages found for "{searchQuery}"</p>
          </div>
        ) : searchResults.length > 0 ? (
          <div>
            <p className="px-4 py-2 text-xs text-theme-tertiary bg-gray-50 dark:bg-gray-700/50">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
            </p>
            {searchResults.map((message) => (
              <Button
                key={message.id}
                variant="ghost"
                size="sm"
                onClick={() => onMessageClick?.(message)}
                className="w-full px-4 py-3 text-left justify-start border-b border-gray-100 dark:border-gray-700"
              >
                <div className="flex items-start justify-between mb-1">
                  <span className="text-sm font-medium text-theme-primary">
                    {message.sender?.name || 'Unknown'}
                  </span>
                  <span className="text-xs text-theme-tertiary">
                    {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                  {highlightMatch(message.content || '', searchQuery)}
                </p>
              </Button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-theme-tertiary">
            <FaIcon icon="magnifying-glass" className="w-12 h-12 mb-3" />
            <p className="text-sm">Type to search messages</p>
          </div>
        )}
      </div>
    </div>
  );
}
